/**
 * API Client for Trade Loop v2.0
 *
 * Uses cookie-based authentication (opaque session_id cookie).
 * Cookies are automatically sent with every request via credentials: 'include'.
 * The backend handles all token management — no frontend refresh logic needed.
 */

import { timezoneHeaders } from '@/utils/timezone';

// Use the same hostname as the browser to ensure cookies work
// If on 127.0.0.1:3000, use 127.0.0.1:8080. If on localhost:3000, use localhost:8080
const getApiBaseUrl = () => {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  // In development, match the hostname to ensure cookies work across origins
  const hostname = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
  return `http://${hostname}:8080`;
};

// Exported so the native origin shim (`./nativeApiOrigin`) resolves relative
// `/api/...` calls against the exact same base this client uses.
export const API_BASE_URL = getApiBaseUrl();

interface ApiRequestOptions extends RequestInit {
  params?: Record<string, string | number | boolean>;
}

class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Makes an authenticated API request with cookie credentials.
 * The session_id httpOnly cookie is sent automatically by the browser.
 * On 401, the error is thrown — AuthContext/ProtectedRoute handles redirect to /login.
 */
export async function apiRequest<T = any>(
  endpoint: string,
  options: ApiRequestOptions = {}
): Promise<T> {
  const { params, ...fetchOptions } = options;

  // Build URL with query parameters
  let url = `${API_BASE_URL}${endpoint}`;
  if (params) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      searchParams.append(key, String(value));
    });
    url += `?${searchParams.toString()}`;
  }

  // Default headers (skip Content-Type for FormData or when no body - browser sets it automatically)
  //
  // X-Timezone rides on every request so any endpoint that reports "this month" can window it on
  // the user's calendar instead of the server's UTC one (see utils/timezone.ts). It is set before
  // the caller's own headers so an explicit override still wins, and it adds no CORS preflight:
  // on web the API is same-origin, and native already sends X-Client-Platform on every call.
  const headers: HeadersInit = {
    ...(fetchOptions.body instanceof FormData || !fetchOptions.body ? {} : { 'Content-Type': 'application/json' }),
    ...timezoneHeaders(),
    ...fetchOptions.headers,
  };

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      headers,
      credentials: 'include', // Sends session_id cookie with every request
    });

    // Handle non-JSON responses
    const contentType = response.headers.get('content-type');
    const isJson = contentType?.includes('application/json');

    if (!response.ok) {
      const errorData = isJson ? await response.json() : await response.text();

      // Handle account locked - logout the user silently
      if (response.status === 403 && errorData?.code === 'ACCOUNT_LOCKED') {
        console.warn('[API] Account locked detected - forcing logout');
        fetch(`${API_BASE_URL}/api/auth/logout`, {
          method: 'POST',
          credentials: 'include',
        }).catch(err => console.error('[API] Logout after lock failed:', err));

        throw new ApiError(
          'Your account has been locked. Please try logging in again for more information.',
          response.status,
          errorData
        );
      }

      throw new ApiError(
        errorData?.error || errorData?.message || 'Request failed',
        response.status,
        errorData
      );
    }

    // Return parsed JSON or null for 204 No Content
    if (response.status === 204) {
      return null as T;
    }

    return isJson ? await response.json() : await response.text();
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    // Network or other errors
    throw new ApiError(
      error instanceof Error ? error.message : 'Network error',
      0
    );
  }
}

/**
 * Convenience methods for common HTTP verbs
 */
export const api = {
  get: <T = any>(endpoint: string, options?: ApiRequestOptions) =>
    apiRequest<T>(endpoint, { ...options, method: 'GET' }),

  post: <T = any>(endpoint: string, data?: any, options?: ApiRequestOptions) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data instanceof FormData ? data : JSON.stringify(data),
    }),

  put: <T = any>(endpoint: string, data?: any, options?: ApiRequestOptions) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  patch: <T = any>(endpoint: string, data?: any, options?: ApiRequestOptions) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: data !== undefined ? JSON.stringify(data) : undefined,
    }),

  delete: <T = any>(endpoint: string, data?: any, options?: ApiRequestOptions) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'DELETE',
      body: data !== undefined ? JSON.stringify(data) : undefined,
    }),
};

/**
 * The most actionable sentence an API failure carries.
 *
 * `ApiError.message` is built from `errorData.error` first, which for a Fastify schema
 * rejection is the bare status label — "Bad Request" — while the sentence naming the offending
 * field sits unread in `message`. Routes that send both use `error` as a machine code
 * ('MISSING_USER') and `message` as the human text, so the same preference is right there too.
 * Prefer `message` whenever it says something `error` does not.
 */
export function describeApiError(error: unknown): string {
  const data = error instanceof ApiError ? error.data : undefined;
  const detail = typeof data?.message === 'string' ? data.message.trim() : '';
  if (detail && detail !== data?.error) return detail;

  if (error instanceof Error && error.message) return error.message;
  return 'Unknown error';
}

export { ApiError };
